import { isNil, zip } from 'lodash';
import { mutateByFieldTypeSync } from './mutate/mutateSync';
import { FormValueChild, ListValuedIntermediateTemplateField } from './types';

export const splitFullyQualifiedPath = (fqPath: string): string[] => {
  // This function splits paths like:
  //    _value.listField._value.2.listChild._value
  // into:
  //    ['_value', 'listField', '_value.2', 'listChild', '_value']
  // The regex matches dots that are not followed by a number, which preserves
  // array indices as a single component. The filter removes the undefined,
  // empty string, and single dot components that are artifacts of regex
  // splitting in JS.
  const regex = /(\.)(?!(\d+))/g;
  return fqPath.split(regex).filter((x) => ![undefined, '', '.'].includes(x));
};

const determineIdx = (
  component: string,
  current: ListValuedIntermediateTemplateField
): string => {
  const idxs = current['_idxs'];
  if (!isNil(idxs)) {
    const idx = idxs.findIndex((idx) => idx === component);
    return `${idx}`;
  } else {
    return component.split('.')[1];
  }
};

export const reduceFullyQualifiedPath = (
  fqPath: string,
  template: FormValueChild
): string | undefined => {
  // NOTE(jacob): I couldn't figure out a good way to avoid using `any` here,
  // since the type of `current` changes as we iterate over the components of
  // the path. This function should still be safe, since we always check isNil
  // before accessing properties of `current`.
  let current = { ...template } as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  const fqComponents: string[] = [];
  const splitPath = splitFullyQualifiedPath(fqPath);

  for (const component of splitPath) {
    const arrayComponentRegex = /(_value|_added)\.(\d+)/;
    const isArrayComponent = arrayComponentRegex.test(component);

    if (isArrayComponent) {
      fqComponents.push(determineIdx(component, current));
    } else if (!component.startsWith('_')) {
      fqComponents.push(component);
    }

    for (const subcomponent of component.split('.')) {
      current = current[subcomponent];
      if (isNil(current)) {
        return undefined;
      }
    }
  }

  return fqComponents.join('.');
};

export const doesComponentMatch = (
  fqComponent?: string,
  condensedComponent?: string
): boolean => {
  if (fqComponent === condensedComponent) {
    return true;
  }
  const isNumericIdx = !isNaN(Number(fqComponent));
  return isNumericIdx && condensedComponent === '<n>';
};

export const doesTemplatePathMatch = (
  fqPath: string,
  condensedPath: string,
  template: FormValueChild
): boolean => {
  const condensedComponents = condensedPath.split('.');
  const fqReduced = reduceFullyQualifiedPath(fqPath, template);

  if (isNil(fqReduced)) {
    return false;
  }

  const fqComponents = fqReduced.split('.');

  const zipped = zip(fqComponents, condensedComponents);
  return zipped.every(([fqComp, condComp]) =>
    doesComponentMatch(fqComp, condComp)
  );
};

export const doesSimplePathMatch = (
  simplePath: string,
  condensedPath: string
): boolean => {
  const condensedComponents = condensedPath.split('.');
  const simpleComponents = simplePath.split('.');

  if (condensedComponents.length !== simpleComponents.length) {
    return false;
  }

  return zip(condensedComponents, simpleComponents).every(
    ([condensedComp, simpleComp]) =>
      doesComponentMatch(simpleComp, condensedComp)
  );
};

export const isChildPath = (childPath: string, parentPath: string): boolean => {
  const childComponents = childPath.split('.');
  const parentComponents = parentPath.split('.');

  const zipped = zip(childComponents, parentComponents);

  return zipped.every(
    ([childComp, parentComp]) =>
      doesComponentMatch(childComp, parentComp) || parentComp === undefined
  );
};

export const doesPathMatchAny = (
  fqPath: string,
  validPaths: string[],
  template: FormValueChild
) => {
  return validPaths.some((validPath) =>
    doesTemplatePathMatch(fqPath, validPath, template)
  );
};

export function getAllPaths(template: FormValueChild): string[] {
  const paths: string[] = [];

  mutateByFieldTypeSync(template, {
    field: (child, fqPath) => {
      if (fqPath) {
        const reducedPath = reduceFullyQualifiedPath(fqPath, template);
        if (reducedPath) {
          paths.push(reducedPath);
        }
      }
      return child;
    },
  });

  return paths.sort();
}

export type ValueType = 'object' | 'array' | 'value';

type NextComponentProps = {
  nextComponent: string;
  valueType: ValueType;
};

export const getNextComponent = (
  path: string,
  currentPath: string
): NextComponentProps | undefined => {
  const components = path.split('.');
  const currentComponents = currentPath === '' ? [] : currentPath.split('.');
  const numDeep = currentComponents.length;

  if (numDeep >= components.length) {
    return undefined;
  }

  const isMatchingParentPath = currentComponents.every((component, index) =>
    doesComponentMatch(component, components[index])
  );
  if (!isMatchingParentPath) {
    return undefined;
  }

  const nextComponent = components[numDeep];
  const followingComponent = components[numDeep + 1];

  const isArrayComponent =
    followingComponent === '<n>' || !isNaN(Number(followingComponent));
  const isObjectComponent =
    !isArrayComponent && followingComponent !== undefined;

  const valueType = isArrayComponent
    ? 'array'
    : isObjectComponent
    ? 'object'
    : 'value';

  return { nextComponent, valueType };
};
